import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import BandwidthCalc from '../components/calculators/BandwidthCalc'
import { Breadcrumbs } from '../components/shared/Breadcrumbs'
import { CalculatorContentWrapper } from '../components/shared/CalculatorContentWrapper'
import { parseURLToBandwidth } from '../utils/urlState'
import { useCalculatorStore } from '../store/calculatorStore'
import { generateBreadcrumbList } from '../utils/schemaHelpers'

export default function BandwidthPage() {
  const setBandwidthInputs = useCalculatorStore(s => s.setBandwidthInputs)

  useEffect(() => {
    const urlInputs = parseURLToBandwidth()
    if (urlInputs) {
      setBandwidthInputs(urlInputs as any)
    }
  }, [setBandwidthInputs])
  return (
    <>
      <Helmet>
        <title>Azure Bandwidth & Egress Cost Calculator UK | AzureCalc.uk</title>
        <meta name="description" content="Calculate Azure bandwidth and data egress costs for UK South. First 100 GB/month free. Tiered pricing for outbound internet and inter-region transfers." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/bandwidth" />
        <meta property="og:title" content="Azure Bandwidth & Egress Cost Calculator UK" />
        <meta property="og:description" content="Free independent Azure Bandwidth & Egress pricing calculator for UK South." />
        <meta property="og:url" content="https://www.azure-calc.co.uk/bandwidth" />
        <meta property="og:image" content="https://www.azure-calc.co.uk/og/azure-calc-og.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Azure Bandwidth & Egress Cost Calculator",
          "description": "Independent Azure Bandwidth & Egress pricing calculator for UK South",
          "url": "https://www.azure-calc.co.uk/bandwidth",
          "applicationCategory": "FinanceApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "GBP"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify(generateBreadcrumbList([{ name: 'Home', url: '/', position: 1 }, { name: 'Azure Bandwidth Calculator', url: '/bandwidth', position: 2 }]))}</script>
      </Helmet>
      <div className="px-6 py-6 w-full">
        <div className="pb-6 mb-8" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
          <Breadcrumbs />
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Azure Bandwidth &amp; Egress Cost Calculator
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
            UK South pricing · First 100 GB/month free · Inbound always free
          </p>
        </div>
        <BandwidthCalc />

        <CalculatorContentWrapper
          eyebrow="Pricing Guide"
          title="Azure egress costs are often an architecture surprise"
          intro={
            <>
              <p className="mb-3">
                Bandwidth costs don't appear in every charge scenario, but when they do, they often catch teams
                off guard because the bill arrives after an infrastructure decision is already committed. Egress
                only accrues when data leaves Azure — inbound transfer is free — but the _direction_ and
                _destination_ of data travel both matter for cost. The first 100 GB/month is free, tiered
                rates apply above that, and design choices like multi-region failover, inter-datacenter
                communication, or CDN miss strategies can inflate the egress bill significantly.
              </p>
              <p>
                This page keeps the calculator as the primary action, then explains the scenarios where
                egress becomes a material cost driver and the design trade-offs that influence it.
              </p>
            </>
          }
          sections={[
            {
              id: 'what-is-egress',
              title: 'What counts as egress (and what doesn\'t)',
              body: (
                <>
                  <p className="mb-3">
                    Egress is any data transfer that leaves the Azure boundary. Inbound data —
                    from the internet, from on-premises, from partners — is free. Within a single
                    region (e.g., between a VM and a nearby storage account in UK South), traffic
                    is free. Egress is charged only when data crosses the Azure edge: to the internet,
                    to on-premises, or to another Azure region.
                  </p>
                  <p>
                    The calculator distinguishes between <strong>internet egress</strong> (outside Azure entirely)
                    and <strong>inter-region egress</strong> (between Azure regions). Both are billable, but at
                    different rates. Internet egress is more expensive because it uses public bandwidth; inter-region
                    is cheaper because Microsoft's backbone handles it internally.
                  </p>
                </>
              ),
            },
            {
              id: 'tiered-pricing',
              title: 'Tiered pricing and the free allowance',
              body: (
                <>
                  <p className="mb-3">
                    The first 100 GB of outbound internet transfer per month is free. Above that, pricing
                    is tiered: per-GB rates drop as volume increases. The tiers are:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>0–100 GB/month: Free</li>
                    <li>100–10,000 GB/month: ~£0.070/GB (higher per-GB rate)</li>
                    <li>10,000–50,000 GB/month: ~£0.067/GB (slight discount)</li>
                    <li>50,000–150,000 GB/month: ~£0.056/GB (meaningful discount)</li>
                    <li>150,000–500,000 GB/month: ~£0.040/GB (substantial discount)</li>
                    <li>500,000+ GB/month: contact Microsoft for volume pricing</li>
                  </ul>
                  <p>
                    The tiered model rewards usage scale but also means small egress charges accumulate slowly
                    until they don't. A 95 GB/month pattern costs nothing; 105 GB/month costs £0.35. The marginal
                    GB is expensive even though you're past the free tier.
                  </p>
                </>
              ),
            },
            {
              id: 'internet-vs-inter-region',
              title: 'Internet egress vs inter-region egress: cost and design trade-offs',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Internet egress</strong> is data leaving Azure for external destinations: user downloads,
                    API calls to third-party services, S3 backups to AWS, etc. This is tiered and more expensive.
                  </p>
                  <p className="mb-3">
                    <strong>Inter-region egress</strong> is traffic between two Azure regions (e.g., UK South to North
                    Europe). This is charged at a flat rate (~£0.016/GB for intra-Europe transfer) and applies
                    whether you're replicating storage, scaling web tier, or failing over a database.
                  </p>
                  <p>
                    The design implication is subtle: multi-region architectures for redundancy or performance
                    are real cost drivers, not cost-free. A database replica in a second region incurs egress
                    charges for replication traffic. A global load-balanced service pays egress for cross-region
                    failover backhaul. Teams often don't model this until it's in the invoice.
                  </p>
                </>
              ),
            },
            {
              id: 'worked-example',
              title: 'Worked example: SaaS platform with backup and CDN miss',
              body: (
                <>
                  <p className="mb-3">
                    Imagine a SaaS platform running in UK South serving 10,000 users worldwide:
                  </p>
                  <ul className="list-disc pl-5 space-y-2 mb-3">
                    <li><strong>Primary API + database:</strong> UK South (intra-region traffic is free)</li>
                    <li><strong>Blob storage + CDN:</strong> UK South with Azure CDN</li>
                    <li><strong>Backup:</strong> Replicated nightly to North Europe (DR policy)</li>
                    <li><strong>Analytics:</strong> Aggregate logs shipped to US East for ML pipeline (third-party contract)</li>
                  </ul>
                  <p className="mb-2">
                    Egress breakdown:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 mb-3">
                    <li>CDN origin shield cache misses to users: ~200 GB/month (tiered, mostly at £0.070/GB = £14/month)</li>
                    <li>Backup replication to North Europe: ~50 GB/month (£0.016/GB = £0.80/month)</li>
                    <li>Log shipment to US East: ~150 GB/month (tiered, at £0.070/GB = £10.50/month)</li>
                    <li><strong>Total:</strong> ~£25/month egress</li>
                  </ul>
                  <p>
                    This isn't huge, but it's material if the platform also has storage and compute costs.
                    More importantly, the backup and analytics transfer were probably never explicitly budgeted
                    or challenged during architecture review.
                  </p>
                </>
              ),
            },
            {
              id: 'cost-avoidance-strategies',
              title: 'Cost avoidance and architecture optimization',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Keep replicas and backups in the same region when possible.</strong> A secondary
                    database in the same region has zero egress cost; moving it to another region for DR incurs
                    per-GB charges indefinitely.
                  </p>
                  <p className="mb-3">
                    <strong>Use CDN aggressively for static content.</strong> Cache misses hit your egress bill;
                    caching strategy beats architectural change for cost reduction.
                  </p>
                  <p className="mb-3">
                    <strong>Route external integrations through ExpressRoute or private endpoints where possible.</strong>
                    Some external API patterns can be moved inside your private network, reducing or eliminating
                    public internet egress.
                  </p>
                  <p>
                    <strong>Batch and compress data before export.</strong> Moving logs, backups, or analytics
                    data off-cloud should be deliberate: compress, deduplicate, and batch transfers to minimize
                    raw GB shipped.
                  </p>
                </>
              ),
            },
            {
              id: 'faq',
              title: 'Common questions',
              body: (
                <>
                  <p className="mb-3">
                    <strong>Does using Azure CDN avoid egress charges?</strong> No. CDN origin fetches from your
                    storage account still count as egress. CDN reduces the number of _origin_ requests (cache hits),
                    but each miss is still billable. The benefit is fewer total egress bytes, not zero egress.
                  </p>
                  <p className="mb-3">
                    <strong>Does ExpressRoute eliminate egress costs?</strong> For private Azure-to-on-premises
                    traffic routed through ExpressRoute, charges are different (ExpressRoute billing applies) and
                    internet egress is bypassed. But you're not replacing egress with free transfer — you're replacing
                    it with ExpressRoute charges. For cloud-to-cloud or cloud-to-internet, egress still applies.
                  </p>
                  <p className="mb-3">
                    <strong>Can I reduce egress by choosing a different region?</strong> Not directly. Egress rates
                    are the same for all Azure regions. However, choosing a region closer to your users reduces
                    the _amount_ of CDN miss traffic, which reduces egress bytes and therefore cost.
                  </p>
                  <p>
                    <strong>What if I'm unsure whether traffic counts as egress?</strong> The rule is straightforward:
                    if data leaves the Azure boundary (to the internet or to another cloud) or crosses a region boundary
                    within Azure, it's egress and will be billed. Intra-region and inbound traffic is free. When in doubt,
                    assume the conservative case and run the calculator.
                  </p>
                </>
              ),
            },
          ]}
          methodologyNote={
            <>
              <p className="mb-2">
                Rates are sourced from the Azure Retail Prices API for UK South in GBP. The calculator
                implements tiered pricing accurately and distinguishes between internet and inter-region egress.
                Actual costs depend on traffic patterns, caching configuration, and backup/replication strategy.
              </p>
              <Link to="/methodology" className="text-[#0078d4] hover:underline">
                How prices are calculated →
              </Link>
            </>
          }
          relatedLinks={[
            { label: 'Networking Calculator', to: '/networking' },
            { label: 'Blob Storage Calculator', to: '/blob-storage' },
            { label: 'Virtual Machines Calculator', to: '/virtual-machines' },
            { label: 'Home', to: '/' },
          ]}
        />
      </div>
    </>
  )
}
